import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchThemeData } from '../admin/themeSlice';
import { setSession } from '../admin/sessionSlice';
import { setBackButtonUrl, setCompanyLogoUrl } from './uiSlice';
import { initStageReport } from '../functions/stageReport';
import { stageTogglesFromTheme } from '../utils/stageToggles';
import { applyGameAssignmentToUserData, gameFromInitStageReport } from '../utils/gameStages';
import { extractReportIdFromResponse, resolveReportsAuthToken, sendReport } from '../functions/sendReport';
import { fetchReport as fetchReportApi } from '../functions/fetchReport';

/** Session-scoped user payload — `gameover` from DB indicates completed play (>0). */
const USER_DATA_KEY = 'userData';

function readStoredUserData() {
  try {
    const stored = sessionStorage.getItem(USER_DATA_KEY);
    if (!stored) return null;
    const user = JSON.parse(stored);
    if (!user || typeof user !== 'object') return null;
    if (user.expiry && Number(user.expiry) < Date.now()) return null;
    return user;
  } catch {
    return null;
  }
}
const uuid4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

const isTrueQueryParam = (value) =>
  typeof value === "string" && value.trim().toLowerCase() === "true";

function normalizeReportId(v) {
  if (v == null) return "";
  const s = String(v).trim();
  return s;
}

function buildDisplayName(d) {
  const raw = (x) => (x == null || String(x).trim().toLowerCase() === 'null' ? '' : String(x).trim());
  const first = raw(d.firstName);
  const last = raw(d.lastName);
  const combined = [first, last].filter(Boolean).join(' ').trim();
  if (combined) return combined;
  const n = raw(d.name);
  if (n && !n.toLowerCase().includes('null')) return n;
  return raw(d.email) || raw(d.employeeId) || raw(d.userId) || 'Guest';
}

function resolveTotalPuzzles(themeData) {
  const total = Math.floor(Number(themeData?.total_puzzles ?? themeData?.puzzle_parts));
  return Number.isFinite(total) && total > 0 ? total : undefined;
}

async function seedStageReportAndAssignment({
  userData,
  totalPuzzles,
  stageToggles,
}) {
  const initResult = await initStageReport({
    token: `${userData.sessionId}&${userData.organizationId}`,
    userId: userData.userId,
    email: userData.email,
    name: userData.name || userData.email,
    userType: userData.role || userData.source || 'ORG_USER',
    reportId: userData.reportId || '',
    themeName: 'Big Picture',
    totalPuzzles,
    stageToggles,
  });
  const game = gameFromInitStageReport(initResult);
  return {
    userData: applyGameAssignmentToUserData(
      { ...userData, stageToggles },
      game
    ),
  };
}

function normalizeReportRole({ role, source }) {
  const src = String(source || '').trim().toUpperCase();
  const r = String(role || '').trim().toUpperCase();
  if (src === 'GUEST_USER') return 'GUEST_USER';
  if (r === 'GUEST_USER') return 'GUEST_USER';
  if (src === 'ORG_USER') {
    if (r === 'PLAYER' || r === 'PLAYERS' || r === '') return 'ORG_USER';
  }
  return r || src || 'ORG_USER';
}

/** URL, login page extras, API payload, or stored session — first non-empty wins (extras override URL). */
function resolveReportId({ queryParams, loginExtras, apiData, storedUser }) {
  const fromExtras = normalizeReportId(loginExtras?.reportId);
  if (fromExtras) return fromExtras;
  const fromUrl =
    normalizeReportId(queryParams.get("reportId")) ||
    normalizeReportId(queryParams.get("reportid")) ||
    normalizeReportId(queryParams.get("report_id"));
  if (fromUrl) return fromUrl;
  if (apiData && typeof apiData === "object") {
    const fromApi =
      normalizeReportId(apiData.reportId) ||
      normalizeReportId(apiData.reportid) ||
      normalizeReportId(apiData.report_id) ||
      normalizeReportId(apiData.gameReportId) ||
      normalizeReportId(apiData.game_report_id);
    if (fromApi) return fromApi;
  }
  if (storedUser && typeof storedUser === "object") {
    const fromStored =
      normalizeReportId(storedUser.reportId) ||
      normalizeReportId(storedUser.reportid) ||
      normalizeReportId(storedUser.report_id);
    if (fromStored) return fromStored;
  }
  return "";
}

/** Reads stage summary via backend and maps to simple gameover flag. */
async function fetchStageGameover({ sessionId, organizationId, userId, gameId = null }) {
  try {
    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
      /\/+$/,
      ""
    );
    if (!backendBase || !sessionId || !organizationId || !userId) return 0;
    const res = await fetch(`${backendBase}/fetchReport`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionId}&${organizationId}`,
      },
      body: JSON.stringify({ userId, ...(gameId ? { gameId } : {}) }),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.success || !data.report) return 0;
    const status = String(data.report.status ?? '').toLowerCase();
    if (status === 'completed') return 1;
    return 0;
  } catch {
    return 0;
  }
}

async function resolveExistingReportId({ userData }) {
  try {
    if (!userData || typeof userData !== 'object') return null;
    if (!userData.userId || !userData.sessionId || !userData.organizationId) return null;
    const token = `${userData.sessionId}&${userData.organizationId}`;
    const resp = await fetchReportApi({ userId: userData.userId, token });
    const report = resp?.report || null;
    const rid =
      report?.reportId ??
      report?.reportid ??
      report?.report_id ??
      report?.id ??
      extractReportIdFromResponse(resp);
    const s = rid != null ? String(rid).trim() : '';
    return s ? s : null;
  } catch {
    return null;
  }
}

async function seedLoginReportIfMissing({ userData }) {
  try {
    if (!userData || typeof userData !== 'object') return null;
    const roleUpper = String(userData.role || userData.source || '').trim().toUpperCase();
    if (roleUpper === 'DEMOBYPASS') return null;
    const hasReportId = userData.reportId != null && String(userData.reportId).trim() !== '';
    if (hasReportId) return String(userData.reportId).trim();

    // If a report row already exists for this user, reuse it (avoid duplicate inserts).
    const existingRid = await resolveExistingReportId({ userData });
    if (existingRid) return existingRid;

    const adminToken = `${userData.sessionId}&${userData.organizationId}`;
    const tokenForReports = resolveReportsAuthToken(userData.token, adminToken);
    if (!tokenForReports) return null;

    const isGuest = roleUpper === 'GUEST_USER';
    const payload = {
      reportId: null,
      points: 0,
      total_score: 0,
      time: 0,
      gameId: userData.gameId,
      name: userData.name,
      email: userData.email || null,
      sessionId: userData.sessionId,
      userId: userData.userId,
      organizationId: userData.organizationId,
      role: roleUpper || 'ORG_USER',
      user_type: roleUpper || 'ORG_USER',
      owner: roleUpper || 'ORG_USER',
      ans: '',
      guestScoreMinimal: isGuest,
      token: tokenForReports,
    };

    const res = await sendReport(payload);
    const rid = extractReportIdFromResponse(res);
    return rid != null && String(rid).trim() !== '' ? String(rid).trim() : null;
  } catch {
    return null;
  }
}

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (loginExtras, { dispatch, rejectWithValue }) => {
    try {
      const extras =
        loginExtras != null && typeof loginExtras === "object" && !Array.isArray(loginExtras)
          ? loginExtras
          : {};
      const queryParams = new URLSearchParams(window.location.search);
      const save = queryParams.get('save');
      const demo = queryParams.get('demo');
      const fromMobileApp = isTrueQueryParam(queryParams.get('fromMobileApp'));

      if (save) {
        let stored = sessionStorage.getItem(USER_DATA_KEY);
        if (!stored) return rejectWithValue('No saved user data found.');
        let userData = JSON.parse(stored);
        const persistedFromMobileApp = isTrueQueryParam(String(userData?.fromMobileApp ?? ""));
        const finalFromMobileApp = fromMobileApp || persistedFromMobileApp;
        if (finalFromMobileApp !== persistedFromMobileApp) {
          userData = { ...userData, fromMobileApp: finalFromMobileApp };
          sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
        }
        const reportId = resolveReportId({
          queryParams,
          loginExtras: extras,
          apiData: null,
          storedUser: userData,
        });
        userData = { ...userData, reportId };
        dispatch(setSession({ sessionId: userData.sessionId, organizationId: userData.organizationId }));
        let themeData = null;
        try {
          const themePayload = await dispatch(fetchThemeData({ themeId: null })).unwrap();
          themeData = themePayload?.data ?? null;
        } catch {
          /* ignore */
        }
        const totalPuzzles = resolveTotalPuzzles(themeData);
        const stageToggles = stageTogglesFromTheme(themeData);
        // Ensure header UI is populated on "save" login flow too.
        dispatch(setCompanyLogoUrl(userData.companyLogoUrl || process.env.REACT_APP_EM_LOGO || null));
        dispatch(setBackButtonUrl(userData.backButtonRedirect || null));
        const go = await fetchStageGameover({
          sessionId: userData.sessionId,
          organizationId: userData.organizationId,
          userId: userData.userId,
          gameId: userData.gameId || null,
        });
        // If already completed, do NOT insert/seed any new report row.
        // Returning users should update existing row only.
        if (go > 0) {
          const existingRid = await resolveExistingReportId({ userData });
          userData = { ...userData, gameover: go, reportId: existingRid || userData.reportId };
        } else {
          // Ensure reports row exists on login so main page can always update it.
          try {
            const rid = await seedLoginReportIfMissing({ userData });
            if (rid) userData = { ...userData, reportId: rid };
          } catch {
            /* ignore */
          }
          userData = { ...userData, gameover: go };
        }
        userData = { ...userData, stageToggles };
        try {
          const seeded = await seedStageReportAndAssignment({
            userData,
            totalPuzzles,
            stageToggles,
          });
          userData = seeded.userData;
        } catch (e) {
          console.warn('initStageReport failed:', e?.message || e);
        }
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
        return userData;
      }

      let userResponseData;
      if (demo) {
        console.log("working with demo now");
        function generateRandomUser() {
          const randomString = 'demo' + Math.floor(Math.random() * 10000);
          return {
            data: {
              data: {
                id: Math.floor(Math.random() * 100000),
                email: `${randomString}@gmail.com`,
                userId: randomString,
                firstName: randomString,
                lastName: randomString,
                employeeId: `EM${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
                sessionId: 'demo'+Math.floor(Math.random() * 100000),
                organizationId: 'demo'+Math.floor(Math.random() * 100000),
                gameId: null,
                role:"demobypass",
                companyLogoUrl: process.env.REACT_APP_EM_LOGO,
                backButtonRedirect: process.env.REACT_APP_BASE_URL,
                gameover: 0,
              }
            }
          };
        }
        const response = generateRandomUser();
        userResponseData = response.data.data;
      } else {
        const otp = queryParams.get('otp');
        const role = queryParams.get('role');
        if (!otp) return rejectWithValue('OTP is required for login.');

        let apiUrl = process.env.REACT_APP_FIREBASE_LOGIN_API;
        const requestData = { otp };
        let source = 'ORG_USER';
        if (role === 'GUEST_USER') {
          apiUrl = process.env.REACT_APP_FIREBASE_GUEST_USER_API;
          requestData.role = 'GUEST_USER';
          source = 'GUEST_USER';
        }

        const clientSecret =
          process.env.REACT_APP_FIREBASE_CLIENT_SECRET ||
          process.env.REACT_APP_CLIENT_SECRET;
        const headers = {
          'client-secret': clientSecret,
          'Content-Type': 'application/json',
          'Idempotency-Key': uuid4(),
        };

        const response = await axios.post(apiUrl, requestData, { headers });
        if (response.data.message !== 'LOGIN SUCCESS') {
          return rejectWithValue('Login failed');
        }
        response.data.data["role"]=source;
        userResponseData = response.data.data;
      }

      // Store session and fetch theme
      dispatch(setSession({ sessionId: userResponseData.sessionId, organizationId: userResponseData.organizationId }));
      let themeData = null;
      try {
        const themePayload = await dispatch(fetchThemeData({ themeId: null })).unwrap();
        themeData = themePayload?.data ?? null;
      } catch {
        /* ignore */
      }
      const totalPuzzles = resolveTotalPuzzles(themeData);
      const stageToggles = stageTogglesFromTheme(themeData);

      const reportId = resolveReportId({
        queryParams,
        loginExtras: extras,
        apiData: userResponseData,
        storedUser: null,
      });

      // Build user data to persist
      let userData = {
        userId: userResponseData.userId || userResponseData.id,
        token: userResponseData.token || "",
        name: buildDisplayName(userResponseData),
        email: userResponseData.email || userResponseData.employeeId,
        sessionId: userResponseData.sessionId,
        organizationId: userResponseData.organizationId,
        gameId: userResponseData.gameId,
        source: demo ? 'DEMO' : 'ORG_USER',
        expiry: Date.now() + 24 * 60 * 60 * 1000,
        role: normalizeReportRole({ role: userResponseData.role, source: demo ? 'DEMO' : 'ORG_USER' }),
        companyLogoUrl: process.env.REACT_APP_EM_LOGO,
        backButtonRedirect: process.env.REACT_APP_BASE_URL,
        gameover: 0,
        fromMobileApp,
        reportId,
        stageToggles,
      };

      // Seed stages row first so fetchReport doesn't return null on first login.

      // Fetch organization details if not demo
      if (!demo) {
        const orgRes = await axios.get(`${process.env.REACT_APP_ORG_API}/${userResponseData.organizationId}`);
         console.log(orgRes);
        if (orgRes.data?.message === 'ORGANIZATIONS_FETCHED_SUCCESSFULLY') {
          console.log(orgRes.data);
          console.log(orgRes.data?.organization.companyLogo);
          if(!orgRes.data?.organization?.companyLogo){
            userData.companyLogoUrl = process.env.REACT_APP_EM_LOGO;
          }else{
            const s3Source = process.env.REACT_APP_S3_SOURCE || "";
            const base = s3Source.endsWith("/") ? s3Source : `${s3Source}/`;
            userData.companyLogoUrl = `${base}${orgRes.data.organization.companyLogo}`;
          }
          userData.backButtonRedirect = `${process.env.REACT_APP_BASE_URL}/game-detail/${userResponseData.gameId}`;
          console.log("setcompany url",userData.companyLogoUrl);
        }
      }
      
      dispatch(setCompanyLogoUrl(userData.companyLogoUrl));
      dispatch(setBackButtonUrl(userData.backButtonRedirect || null));

      // Ensure reports row exists on login so main page can always update it.
      if (!demo) {
        const go = await fetchStageGameover({
          sessionId: userData.sessionId,
          organizationId: userData.organizationId,
          userId: userData.userId,
          gameId: userData.gameId || null,
        });
        userData.gameover = go;
        if (go > 0) {
          // Already played — never insert a new report row here.
          const existingRid = await resolveExistingReportId({ userData });
          if (existingRid) userData.reportId = existingRid;
        } else {
          // Not completed yet — seed via sendReport first, then initStageReport with reportId.
          try {
            const rid = await seedLoginReportIfMissing({ userData });
            if (rid) userData.reportId = rid;
          } catch {
            /* ignore */
          }
        }
      }

      try {
        const seeded = await seedStageReportAndAssignment({
          userData,
          totalPuzzles,
          stageToggles,
        });
        userData = seeded.userData;
      } catch (e) {
        console.warn('initStageReport failed:', e?.message || e);
      }
      
      sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
      return userData;
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem(USER_DATA_KEY);
      }
      const data = error.response?.data;
      const apiMsg =
        (typeof data?.message === 'string' && data.message) ||
        data?.err?.message;
      return rejectWithValue(apiMsg || error.message);
    }
  }
);
const persistedUserData = readStoredUserData();

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: persistedUserData,
    status: persistedUserData ? 'succeeded' : 'idle',
    error: null
  },
  reducers: {
    logout: state => {
      state.user = null;
      sessionStorage.removeItem(USER_DATA_KEY);
    }
    ,
    /** After first `sendReport` POST, API may return `reportId` — store for subsequent PUTs. */
    mergeReportId: (state, action) => {
      const id = action.payload;
      if (!state.user || id == null || String(id).trim() === '') return;
      state.user = { ...state.user, reportId: String(id).trim() };
      try {
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(state.user));
      } catch {
        /* ignore */
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.user = null;
      });
  }
});

export const { logout, mergeReportId } = authSlice.actions;
export default authSlice.reducer;